import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useContext } from 'react';
import ChevronRight from '@mui/icons-material/ChevronRight';
import AirportMap from './components/AirportMap';
import ControllerList from './components/ControllerList';
import DeviceTypeSelector from './components/DeviceTypeSelector';
import DeviceList from './components/DeviceList';
import PasInventoryUpload from './components/PasInventoryUpload';
import api from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import AppShell from '../../components/layout/AppShell';
import { PageHeader, PageLoader } from '../../components/ui';

const PAS_DEVICE_TYPES = [
    'Mainframe',
    'Speaker',
    'Loop',
    'Line Array',
    'Noise Sensor',
    'Digital Audio Bridge',
    'Mic Station',
    'IED',
];

export const locationMatchesArea = (location, area) => {
    const lname = (location?.name || '').toLowerCase();
    const lcode = (location?.code || '').toLowerCase();
    const aname = (area?.name || '').toLowerCase();
    const aliases = Array.isArray(area?.locationAliases) ? area.locationAliases : [];

    if (lname.includes(aname) || aname.includes(lname) || lcode === aname) return true;
    if (aliases.some(alias => lname.includes(alias) || lcode.includes(alias))) return true;
    if (aname.includes('concourse a') && lname.includes('concourse a')) return true;
    if (aname.includes('concourse b') && lname.includes('concourse b')) return true;
    if (aname.includes('concourse c') && (lname.includes('concourse c') || lname.includes('south node') || lcode.includes('south_node') || lname.includes('north node') || lcode.includes('north_node'))) return true;
    if (aname.includes('terminal') && (lname.includes('mtb') || lname.includes('terminal'))) return true;
    if (aname.includes('mtb') && (lname.includes('mtb') || lname.includes('terminal'))) return true;
    if (aname.includes('south node') && (lname.includes('south node') || lcode.includes('south_node'))) return true;

    return false;
};

const PASMainPage = () => {
    const { role } = useContext(AuthContext);
    const [currentView, setCurrentView] = useState('map'); // 'map', 'controllers', 'device-types', 'device-list'
    const [selectedArea, setSelectedArea] = useState(null);
    const [controllers, setControllers] = useState([]);
    const [selectedController, setSelectedController] = useState(null);
    const [controllerDevices, setControllerDevices] = useState([]);
    const [selectedDeviceType, setSelectedDeviceType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [topologyData, setTopologyData] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const scrollPosition = useRef(0);

    useEffect(() => {
        fetchTopology();
    }, []);

    useLayoutEffect(() => {
        if (currentView === 'map') {
            window.scrollTo(0, scrollPosition.current);
        }
    }, [currentView]);

    const fetchTopology = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/topology/tree');
            setTopologyData(response.data);
        } catch (error) {
            console.error('Failed to fetch topology:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAreaSelect = (area) => {
        scrollPosition.current = window.scrollY; // Save current scroll position
        setSelectedArea(area);
        setSelectedController(null);
        setSelectedDeviceType(null);
        setControllerDevices([]);
        setCurrentView('controllers');
        window.scrollTo(0, 0); // Scroll to top for detail view

        // Flatten all topology nodes for flexible matching (some areas map to multiple nodes)
        const flatten = (nodes = []) => nodes.reduce((acc, node) => {
            acc.push(node);
            if (node.children && node.children.length) {
                acc.push(...flatten(node.children));
            }
            return acc;
        }, []);

        const allNodes = flatten(topologyData || []);

        // Filter controllers for the selected area with looser matching by name/id and keywords
        const areaNode = allNodes.find(loc => locationMatchesArea(loc, area));

            if (areaNode) {
                // Collect controllers from the entire subtree rooted at areaNode (recursive)
                const collectNodes = (node) => {
                    const nodes = [node];
                    if (node.children && node.children.length) {
                        node.children.forEach(ch => {
                            nodes.push(...collectNodes(ch));
                        });
                    }
                    return nodes;
                };

                const subtreeNodes = collectNodes(areaNode);
                let allControllers = [];
                subtreeNodes.forEach(n => {
                    if (n.controllers && n.controllers.length) {
                        allControllers = [...allControllers, ...n.controllers];
                    }
                });

                setControllers(allControllers);
            } else {
            // Fallback: If area is Concourse C, gather all Concourse C nodes (west/east/north) controllers
            const aname = (area.name || '').toLowerCase();
            if (aname.includes('concourse c')) {
                let allControllers = [];
                allNodes.forEach(node => {
                    const lname = (node.name || '').toLowerCase();
                    if (lname.includes('concourse c')) {
                        allControllers = [...allControllers, ...(node.controllers || [])];
                    }
                });
                setControllers(allControllers);
            } else if (aname.includes('south node')) {
                let allControllers = [];
                allNodes.forEach(node => {
                    const lname = (node.name || '').toLowerCase();
                    const lcode = (node.code || '').toLowerCase();
                    if (lname.includes('south node') || lcode.includes('south_node')) {
                        allControllers = [...allControllers, ...(node.controllers || [])];
                    }
                });
                setControllers(allControllers);
            } else {
                setControllers([]);
            }
        }
    };

    const resetToOverview = () => {
        setSelectedArea(null);
        setSelectedController(null);
        setSelectedDeviceType(null);
        setControllerDevices([]);
        setControllers([]);
        setErrorMessage('');
        setCurrentView('map');
    };

    const handleBack = () => {
        setErrorMessage('');

        if (currentView === 'device-list') {
            setSelectedDeviceType(null);
            setCurrentView('device-types');
            return;
        }

        if (currentView === 'device-types') {
            setSelectedController(null);
            setSelectedDeviceType(null);
            setControllerDevices([]);
            setCurrentView('controllers');
            return;
        }

        if (currentView === 'controllers') {
            resetToOverview();
        }
    };

    const isLifeLine = (controller) =>
        (controller?.description || controller?.name || '').toLowerCase().includes('life line');

    const fetchControllerDevices = async (controller) => {
        try {
            setLoading(true);
            const controllerId = controller._id || controller.id;
            try {
                const response = await api.get(`/api/controllers/${controllerId}/devices`);
                setControllerDevices(response.data || []);
            } catch (primaryError) {
                console.warn('Primary devices endpoint failed, falling back to /api/devices', primaryError);
                const fallback = await api.get('/api/devices', {
                    params: { controllerId }
                });
                setControllerDevices(fallback.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch controller devices:', error);
            // Keep any preloaded devices if API fails (auth/routing issues)
            if (!controller?.devices || controller.devices.length === 0) {
                setErrorMessage('Unable to load devices for this controller right now.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleControllerSelect = async (controller) => {
        if (isLifeLine(controller)) {
            return; // Lifelines have no devices; do not navigate away
        }

        setSelectedController(controller);
        // Preload any devices already attached on the topology node (fallback if API blocked)
        setSelectedDeviceType(null);
        setControllerDevices(controller?.devices || []);
        setErrorMessage('');
        setCurrentView('device-types');
        await fetchControllerDevices(controller);
    };

    const deviceTypeCounts = useMemo(() => {
        const counts = {};
        controllerDevices.forEach((device) => {
            const type = device.deviceType || 'Unknown';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [controllerDevices]);

    const availableDeviceTypes = useMemo(
        () => [...new Set([...PAS_DEVICE_TYPES, ...Object.keys(deviceTypeCounts)])].sort(),
        [deviceTypeCounts]
    );

    const filteredDevices = useMemo(() => {
        if (!selectedDeviceType) return [];
        return controllerDevices.filter((device) => device.deviceType === selectedDeviceType);
    }, [controllerDevices, selectedDeviceType]);

    const handleDeviceTypeSelect = (type) => {
        setSelectedDeviceType(type);
        setCurrentView('device-list');
    };

    const handleControllerChanged = async (updatedController) => {
        const updatedId = updatedController?._id || updatedController?.id;
        if (selectedController && String(selectedController._id || selectedController.id) === String(updatedId)) {
            setSelectedController(updatedController);
        }
        setControllers((current) => {
            const exists = current.some((controller) => String(controller._id || controller.id) === String(updatedId));
            return exists
                ? current.map((controller) =>
                    String(controller._id || controller.id) === String(updatedId) ? updatedController : controller
                )
                : [...current, updatedController];
        });
        await fetchTopology();
    };

    const handleControllerDeleted = async (deletedController) => {
        const deletedId = deletedController?._id || deletedController?.id;
        setControllers((current) => current.filter((controller) =>
            String(controller._id || controller.id) !== String(deletedId)
        ));
        if (selectedController && String(selectedController._id || selectedController.id) === String(deletedId)) {
            setSelectedController(null);
            setSelectedDeviceType(null);
            setControllerDevices([]);
            setCurrentView('controllers');
        }
        await fetchTopology();
    };

    // Built from the drill-down state so the trail can never disagree with the view.
    const crumbs = [
        { label: 'Overview', onClick: resetToOverview, active: currentView === 'map' },
        selectedArea && {
            label: selectedArea.name,
            active: currentView === 'controllers',
            onClick: () => {
                setCurrentView('controllers');
                setSelectedController(null);
                setSelectedDeviceType(null);
            },
        },
        selectedController && {
            label: selectedController.description || selectedController.name,
            active: currentView === 'device-types',
            onClick: () => {
                setCurrentView('device-types');
                setSelectedDeviceType(null);
            },
        },
        selectedDeviceType && { label: selectedDeviceType, active: true },
    ].filter(Boolean);

    return (
        <>
            <PageHeader
                title="PAS Interface"
                subtitle="Public Address System management"
                backTo="/"
            />

            <AppShell>
                {/* Scrolls sideways on a phone rather than wrapping into a ragged block. */}
                <nav aria-label="Breadcrumb" className="-mx-4 mb-5 px-4">
                    <ol className="scroll-x flex items-center gap-2 pb-1 text-sm">
                        {crumbs.map((crumb, i) => (
                            <li key={`${crumb.label}-${i}`} className="flex shrink-0 items-center gap-2">
                                {i > 0 && (
                                    <ChevronRight
                                        style={{ fontSize: 16 }}
                                        aria-hidden="true"
                                        className="text-muted-fg"
                                    />
                                )}
                                {crumb.onClick && !crumb.active ? (
                                    <button
                                        type="button"
                                        onClick={crumb.onClick}
                                        className="cursor-pointer whitespace-nowrap rounded px-1 py-0.5 text-muted-fg transition-colors duration-fast hover:text-accent"
                                    >
                                        {crumb.label}
                                    </button>
                                ) : (
                                    <span
                                        aria-current={crumb.active ? 'page' : undefined}
                                        className="whitespace-nowrap px-1 py-0.5 font-medium text-foreground"
                                    >
                                        {crumb.label}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ol>
                </nav>

                {currentView === 'map' && (
                    <>
                        {role === 'admin' && <PasInventoryUpload onImported={fetchTopology} />}
                        {loading
                            ? <PageLoader label="Loading airport data…" />
                            : <AirportMap onAreaSelect={handleAreaSelect} />}
                    </>
                )}

                {currentView === 'controllers' && selectedArea && (
                    <ControllerList
                        controllers={controllers}
                        areaName={selectedArea.name}
                        onBack={handleBack}
                        onSelectController={handleControllerSelect}
                        onControllerChanged={handleControllerChanged}
                        onControllerDeleted={handleControllerDeleted}
                    />
                )}

                {currentView === 'device-types' && selectedController && (
                    <DeviceTypeSelector
                        controller={selectedController}
                        deviceTypes={availableDeviceTypes}
                        deviceCounts={deviceTypeCounts}
                        onSelectType={handleDeviceTypeSelect}
                        onControllerChanged={handleControllerChanged}
                        onControllerDeleted={handleControllerDeleted}
                        onBack={handleBack}
                        loading={loading}
                        errorMessage={errorMessage}
                    />
                )}

                {currentView === 'device-list' && selectedController && selectedDeviceType && (
                    <DeviceList
                        controller={selectedController}
                        devices={filteredDevices}
                        deviceType={selectedDeviceType}
                        onBack={handleBack}
                        onDevicesChanged={() => fetchControllerDevices(selectedController)}
                        loading={loading}
                        errorMessage={errorMessage}
                    />
                )}
            </AppShell>
        </>
    );
};

export default PASMainPage;
